import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Phone, MessageCircle, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import Header from './Header';
import Footer from './Footer';
import WhatsAppButton from './WhatsAppButton';
import siteConfig from '../config';

const brandInfo = {
  'mercedes-benz': {
    title: 'Mercedes-Benz Repair and Servicing',
    description: 'Specialized Mercedes-Benz service in Khan Market, New Delhi',
    content: {
      intro: 'Expert Mercedes-Benz repair and maintenance services using genuine parts and advanced diagnostic equipment.',
      services: [
        'Engine diagnostics and repair',
        'Airmatic suspension repair',
        'Brake system maintenance',
        'AC and cooling system repair',
        'Transmission service',
        'Annual maintenance packages'
      ],
      reasons: [
        'Dashboard warning lights',
        'Suspension issues',
        'Engine performance problems',
        'Brake concerns',
        'AC not cooling properly',
        'Regular scheduled maintenance'
      ],
      inspection: 'Our Mercedes-Benz inspection includes computerized diagnostics, fluid level checks, brake inspection, suspension assessment, and comprehensive system scan.'
    }
  },
  'bmw': {
    title: 'BMW Repair and Servicing',
    description: 'Professional BMW service in Khan Market, New Delhi',
    content: {
      intro: 'Specialized BMW repair and maintenance using OEM parts and manufacturer-recommended procedures.',
      services: [
        'Engine diagnostics and repair',
        'Air suspension service',
        'Brake system repair',
        'Cooling system maintenance',
        'Transmission service',
        'Scheduled service packages'
      ],
      reasons: [
        'Check engine light',
        'Suspension problems',
        'Brake warning lights',
        'Overheating issues',
        'Transmission concerns',
        'Regular maintenance due'
      ],
      inspection: 'BMW inspection covers computerized diagnostics, brake system check, suspension evaluation, fluid analysis, and complete system diagnostics.'
    }
  },
  'audi': {
    title: 'Audi Repair and Servicing',
    description: 'Expert Audi service in Khan Market, New Delhi',
    content: {
      intro: 'Professional Audi repair and servicing using genuine parts and advanced diagnostic tools.',
      services: [
        'Engine diagnostics and repair',
        'Suspension service',
        'Brake system maintenance',
        'AC and climate control repair',
        'Transmission service',
        'Annual service packages'
      ],
      reasons: [
        'Dashboard warning indicators',
        'Suspension noise or issues',
        'Brake system concerns',
        'AC problems',
        'Engine performance issues',
        'Scheduled maintenance'
      ],
      inspection: 'Audi inspection includes diagnostic scan, brake check, suspension assessment, fluid inspection, and comprehensive system evaluation.'
    }
  }
};

const BrandServicePage = () => {
  const { brand } = useParams();
  const info = brandInfo[brand];
  
  if (!info) {
    return <div>Brand not found</div>;
  }

  const whatsappLink = `https://wa.me/${siteConfig.contact.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hello Moto Craft, I need ${info.title} service`)}`;

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <div className="pt-24 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-yellow-400 hover:text-yellow-300 mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">{info.title}</h1>
          <p className="text-xl text-gray-400 mb-8">{info.description}</p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">About Our Service</h2>
                <p className="text-gray-300 leading-relaxed">{info.content.intro}</p>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Services We Offer</h2>
                <ul className="space-y-3">
                  {info.content.services.map((service, index) => (
                    <li key={index} className="flex items-start text-gray-300">
                      <span className="text-yellow-400 mr-3">✓</span>
                      {service}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Common Reasons to Book</h2>
                <ul className="space-y-3">
                  {info.content.reasons.map((reason, index) => (
                    <li key={index} className="flex items-start text-gray-300">
                      <span className="text-yellow-400 mr-3">•</span>
                      {reason}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">What Our Inspection Covers</h2>
                <p className="text-gray-300 leading-relaxed">{info.content.inspection}</p>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-2xl p-8 sticky top-24">
                <h3 className="text-2xl font-bold text-white mb-6">Book Service</h3>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Location</p>
                    <p className="text-white">Khan Market, New Delhi</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Hours</p>
                    <p className="text-white">{siteConfig.hours}</p>
                    <p className="text-red-400 text-sm">Sunday closed</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Phone</p>
                    <p className="text-white">{siteConfig.contact.phone}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <a href={`tel:${siteConfig.contact.phone}`} className="block">
                    <Button className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold py-6">
                      <Phone className="w-5 h-5 mr-2" />
                      Call Now
                    </Button>
                  </a>
                  
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="block">
                    <Button className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-6">
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                </div>

                <p className="text-green-400 text-sm mt-4 text-center">✓ Walk-ins welcome</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default BrandServicePage;
